<template>
    <div class="site-width uk-animation-fade">
        <Seo
            :canonical="'/family-registration/' + slug"
            :title="offer ? offer.name : 'Anmeldung'" />

        <div class="menu-headline-distance" />

        <!-- Loading -->
        <div v-if="loading" class="uk-text-center">
            <div uk-spinner />
        </div>

        <!-- Invalid token -->
        <template v-else-if="tokenInvalid">
            <div
                class="uk-card uk-card-alternative uk-padding site-width single uk-text-center">
                <SiteHeadline
                    switch-headline
                    is-main
                    headline="Ungültiger Zugangscode."
                    message="Bitte verwende den Link aus deiner Einladung." />
            </div>
        </template>

        <!-- Error -->
        <template v-else-if="error || !offer">
            <div
                class="uk-card uk-card-alternative uk-padding site-width single uk-text-center">
                <SiteHeadline
                    switch-headline
                    is-main
                    headline="Angebot nicht gefunden."
                    message="Bitte versuche es später erneut." />
            </div>
        </template>

        <!-- Success -->
        <template v-else-if="success">
            <div
                class="uk-card uk-card-alternative uk-padding site-width single uk-text-center">
                <SiteHeadline
                    is-main
                    switch-headline
                    :message="'Danke, ' + form.forename + '!'"
                    headline="Deine Anmeldung wurde erfolgreich übermittelt. Wir melden uns bei dir mit weiteren Informationen." />
            </div>
            <div class="uk-margin-large-top uk-text-center data-struct">
                <div>
                    <span class="text-bold">Erwachsene:</span> {{ form.adults }}
                </div>
                <div v-if="form.children > 0">
                    <span class="text-bold">Kinder:</span> {{ form.children }}
                </div>
            </div>
        </template>

        <!-- Main: two column layout -->
        <template v-else>
            <div class="uk-grid uk-grid-large" uk-grid>
                <!-- Left: Info -->
                <div class="uk-width-1-2@m">
                    <div class="eyebrow">Für eine Kindheit ohne Krebs</div>
                    <h1 class="hero-headline">
                        Die Auszeit, die ihr euch verdient habt.
                    </h1>
                    <p class="hero-text">
                        Direkt auf dem Deich an der Wurster Nordseeküste, mit
                        Blick aufs Meer – Cancel Cancer schenkt Familien
                        krebskranker Kinder eine echte Auszeit im
                        <strong>Upleven in Wremen</strong> – dem einzigen Hotel
                        Deutschlands, das direkt auf dem Deich steht.
                    </p>

                    <div class="info-items">
                        <div class="info-item">
                            <div class="info-icon">📅</div>
                            <div>
                                <div class="info-label">Zeitraum</div>
                                <div class="info-value">
                                    6. bis 9. August · 3 Nächte
                                </div>
                            </div>
                        </div>
                        <div class="info-item">
                            <div class="info-icon">🍽️</div>
                            <div>
                                <div class="info-label">Verpflegung</div>
                                <div class="info-value">
                                    Vollpension inklusive
                                </div>
                            </div>
                        </div>
                        <div class="info-item">
                            <div class="info-icon">🌊</div>
                            <div>
                                <div class="info-label">Lage</div>
                                <div class="info-value">
                                    Direkte Deich- und Strandlage, Wattenmeer
                                </div>
                            </div>
                        </div>
                        <div class="info-item">
                            <div class="info-icon">🎯</div>
                            <div>
                                <div class="info-label">Programm</div>
                                <div class="info-value">
                                    Kinderyoga, Wattwanderung, Lagerfeuer & mehr
                                </div>
                            </div>
                        </div>
                    </div>

                    <a
                        href="/assets/upleven.pdf"
                        target="_blank"
                        rel="noopener"
                        class="btn-info">
                        Alle Infos als PDF →
                    </a>
                </div>

                <!-- Right: Form -->
                <div class="uk-width-1-2@m">
                    <div class="uk-card uk-card-alternative uk-padding">
                        <h2 class="form-title uk-margin-bottom">
                            Jetzt anmelden
                        </h2>
                        <p class="form-sub">
                            Die Plätze sind begrenzt. Wir melden uns zur
                            Bestätigung.
                        </p>

                        <span id="error-box" />
                        <InfoBox
                            mode="danger"
                            slim
                            medium
                            v-if="formError"
                            class="uk-margin-medium-bottom uk-animation-fade">
                            {{ formError }}
                        </InfoBox>

                        <!-- Name -->
                        <div class="uk-grid uk-grid-small" uk-grid>
                            <div class="uk-width-1-2">
                                <label class="field-label">Vorname</label>
                                <input
                                    v-model="form.forename"
                                    class="uk-input field-input"
                                    type="text" />
                            </div>
                            <div class="uk-width-1-2">
                                <label class="field-label">Nachname</label>
                                <input
                                    v-model="form.surname"
                                    class="uk-input field-input"
                                    type="text" />
                            </div>
                        </div>

                        <!-- Email + Phone -->
                        <div
                            class="uk-grid uk-grid-small uk-margin-top"
                            uk-grid>
                            <div class="uk-width-1-2">
                                <label class="field-label"
                                    >E-Mail Adresse</label
                                >
                                <input
                                    v-model="form.email"
                                    class="uk-input field-input"
                                    :class="{'input-error': emailInvalid}"
                                    type="email"
                                    @blur="validateEmail" />
                                <div v-if="emailInvalid" class="field-error">
                                    Bitte eine gültige E-Mail-Adresse eingeben.
                                </div>
                            </div>
                            <div class="uk-width-1-2">
                                <label class="field-label">Telefon</label>
                                <input
                                    v-model="form.phone"
                                    class="uk-input field-input"
                                    type="tel" />
                            </div>
                        </div>

                        <!-- Adults + Children -->
                        <div
                            class="uk-grid uk-grid-small uk-margin-top"
                            uk-grid>
                            <div class="uk-width-1-2">
                                <label class="field-label"
                                    >Anzahl Erwachsene</label
                                >
                                <select
                                    v-model="form.adults"
                                    class="uk-select field-input">
                                    <option v-for="n in 10" :key="n" :value="n">
                                        {{ n }}
                                    </option>
                                </select>
                            </div>
                            <div class="uk-width-1-2">
                                <label class="field-label">Anzahl Kinder</label>
                                <select
                                    v-model="form.children"
                                    class="uk-select field-input">
                                    <option
                                        v-for="n in [
                                            0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
                                        ]"
                                        :key="n"
                                        :value="n">
                                        {{ n }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <!-- Notes -->
                        <div class="uk-margin-top">
                            <label class="field-label"
                                >Anmerkungen (optional)</label
                            >
                            <textarea
                                v-model="form.notes"
                                class="uk-textarea field-input"
                                rows="3"
                                style="height: auto" />
                        </div>

                        <!-- Submit -->
                        <div class="uk-margin-medium-top uk-text-center">
                            <Button
                                is-large
                                has-action
                                :action="submit"
                                :disabled="submitting"
                                mode="success"
                                class="uk-width-1-1">
                                {{
                                    submitting
                                        ? 'Wird gesendet...'
                                        : 'Jetzt Anmelden →'
                                }}
                            </Button>
                        </div>

                        <div
                            class="uk-margin-top uk-text-center consent-text">
                            Mit der Anmeldung akzeptierst du unsere
                            Teilnahmebedingungen und die Datenschutzerklärung.
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <div class="uk-margin-large-top" />
    </div>
</template>

<script>
import Seo from '../../components/Seo.vue';
import SiteHeadline from '@/components/SiteHeadline.vue';
import Button from '../../components/Button.vue';
import InfoBox from '@/components/InfoBox.vue';
import axios from 'axios';
import UIkit from 'uikit';

export default {
    name: 'FamilyRegistration',
    components: {Seo, SiteHeadline, Button, InfoBox},
    data() {
        return {
            loading: true,
            error: null,
            tokenInvalid: false,
            offer: null,
            success: false,
            submitting: false,
            formError: null,
            emailInvalid: false,
            form: {
                forename: '',
                surname: '',
                email: '',
                phone: '',
                adults: 2,
                children: 1,
                notes: '',
            },
        };
    },
    computed: {
        slug() {
            return this.$route.params.slug;
        },
        token() {
            return this.$route.query.token;
        },
    },
    created() {
        this.loadOffer();
    },
    methods: {
        loadOffer() {
            this.loading = true;
            axios
                .get(
                    this.getBaseUrl + '/api/family-registration/' + this.slug,
                    {
                        params: {token: this.token},
                    }
                )
                .then((res) => {
                    this.offer = res.data;
                    this.loading = false;
                })
                .catch((e) => {
                    if (e.response && e.response.status === 403) {
                        this.tokenInvalid = true;
                    } else {
                        this.error = e;
                    }
                    this.loading = false;
                });
        },
        validateEmail() {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            this.emailInvalid =
                this.form.email.length > 0 && !re.test(this.form.email);
        },
        submit() {
            this.formError = null;

            if (
                !this.form.forename ||
                !this.form.surname ||
                !this.form.email ||
                !this.form.phone
            ) {
                this.formError = 'Bitte fülle alle Pflichtfelder aus.';
                UIkit.scroll('#error-box', {offset: 100}).scrollTo(
                    '#error-box'
                );
                return;
            }

            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!re.test(this.form.email)) {
                this.emailInvalid = true;
                this.formError = 'Bitte eine gültige E-Mail-Adresse eingeben.';
                UIkit.scroll('#error-box', {offset: 100}).scrollTo(
                    '#error-box'
                );
                return;
            }

            this.submitting = true;

            axios
                .post(
                    this.getBaseUrl +
                        '/api/family-registration/' +
                        this.slug +
                        '/register',
                    {
                        forename: this.form.forename,
                        surname: this.form.surname,
                        email: this.form.email,
                        phone: this.form.phone,
                        adults: this.form.adults,
                        children: this.form.children,
                        notes: this.form.notes || undefined,
                    },
                    {params: {token: this.token}}
                )
                .then(() => {
                    this.success = true;
                    window.scrollTo({top: 0, behavior: 'smooth'});
                })
                .catch((e) => {
                    this.formError =
                        'Ein Fehler ist aufgetreten. Bitte versuche es erneut.';
                    console.error(e);
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.eyebrow {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #2a9d6e;
    margin-bottom: 12px;
}

.hero-headline {
    color: #fff;
    font-size: 2rem;
    font-weight: 700;
    line-height: 1.15;
    margin: 0 0 20px;
}

.accent {
    color: #2a9d6e;
    font-style: italic;
}

.hero-text {
    font-size: 17px;
    line-height: 1.7;
    opacity: 0.85;
    margin-bottom: 28px;
}

.info-items {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-bottom: 28px;
}

.info-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.info-icon {
    font-size: 20px;
    flex-shrink: 0;
    margin-top: 2px;
}

.info-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    opacity: 0.6;
    margin-bottom: 2px;
}

.info-value {
    font-size: 15px;
    font-weight: 500;
}

.btn-info {
    display: inline-block;
    border: 2px solid #2a9d6e;
    color: #2a9d6e !important;
    padding: 11px 28px;
    border-radius: 50px;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none !important;
    transition: background 0.2s, color 0.2s;

    &:hover {
        background: #2a9d6e;
        color: #fff !important;
    }
}

.form-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #ddd;
}

.form-sub {
    font-size: 14px;
    opacity: 0.6;
    margin: -8px 0 16px;
}

.field-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    opacity: 0.7;
    margin-bottom: 6px;
}

.field-input {
    background: #1e1e26 !important;
    border: 1px solid #333 !important;
    color: #fff !important;
    border-radius: 6px;
    height: 48px;

    &:focus {
        border-color: #2a9d6e !important;
        outline: none;
    }
}

.input-error {
    border-color: #f44336 !important;
}

.field-error {
    font-size: 12px;
    color: #f44336;
    margin-top: 4px;
}

.data-struct {
    font-size: 20px;
}
.text-bold {
    font-weight: 500;
}

.consent-text {
    font-size: 13px;
    opacity: 0.5;
}
</style>
